/**
 * buscar.ts — filtrar lo que ya está en pantalla, sin volver a la base.
 *
 * ── ▶ POR QUÉ ESTOS FILTROS SON DEL CLIENTE, Y LOS DEL DIARIO NO ──────────
 *
 * El diario y el mayor filtran en la BASE, con periodo y con tope, porque la
 * historia de una empresa no cabe y no hace falta entera. Acá es al revés y
 * conviene decirlo para que nadie «arregle» esto mandándolo al servidor:
 *
 *   · El catálogo **ya está traído entero**, en un contexto, porque lo
 *     necesitan tres pantallas y el formulario de asiento lo necesita completo
 *     para poder ofrecer cualquier cuenta. Filtrarlo en la base sería una
 *     segunda llamada para un dato que ya está en memoria — y dejaría al
 *     selector de cuentas del asiento viendo un catálogo recortado.
 *   · La consola trae las empresas del operador, que son unidades, no miles.
 *
 * O sea: **el filtro de acá es para que una persona encuentre algo entre 123
 * filas, no para que la consulta pese menos.** Son dos problemas distintos y
 * la misma palabra.
 *
 * ── Lo que se puede probar sin base ni red, se prueba sin base ni red ─────
 *
 * Misma división que `foto.ts` y `apertura.ts`: acá vive la regla, y las
 * pantallas solo la usan. Los tipos son estructurales a propósito —lo mínimo
 * que cada filtro mira— para que este archivo no dependa de `datos/libros.ts`.
 */

/**
 * El texto, comparable.
 *
 * ▶ SIN TILDES, y no es un adorno. Este catálogo tiene «Depreciación»,
 * «Amortización» y «Participación»: quien busca escribe `depreciacion` sin
 * pensarlo, y una búsqueda que no encuentra nada se lee como «no existe», no
 * como «lo escribió distinto».
 *
 * `NFD` separa la letra de su tilde y el rango de marcas la borra. La ñ
 * sobrevive como `n`, que es lo que hace falta: quien busca «pequenas» también
 * tiene que encontrar «pequeñas».
 */
export function sinTildes(texto: string): string {
  // Se borran por PROPIEDAD y no por rango de códigos. Un rango escrito con
  // los caracteres de verdad hace lo mismo hoy y son marcas invisibles: nadie
  // ve en una revisión que alguien las cambió, y un editor que «limpie» el
  // archivo se las puede llevar sin dejar rastro en el diff.
  return texto.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();
}

/** ¿Alguno de estos textos contiene la aguja? Aguja vacía: todo coincide. */
export function coincide(aguja: string, ...textos: readonly (string | null)[]): boolean {
  const a = sinTildes(aguja.trim());
  if (a === '') return true;
  return textos.some((t) => t !== null && sinTildes(t).includes(a));
}

/* ═══════════════════════════════════════════════════════════════════════════
   El catálogo
   ═══════════════════════════════════════════════════════════════════════════ */

/** Lo mínimo que el filtro del catálogo necesita ver de una cuenta. */
export interface CuentaFiltrable {
  readonly codigo: string;
  readonly padre: string | null;
  readonly nombre: string;
  readonly nombreEn: string;
  readonly tipo: string;
  readonly permiteMovimiento: boolean;
  readonly activa: boolean;
}

export interface CriterioDeCuentas {
  /** Código o nombre, en cualquiera de los dos idiomas. */
  readonly texto: string;
  /** `asset`, `liability`… o `''` para todos. */
  readonly tipo: string;
  /** Solo las que pueden recibir un movimiento. */
  readonly soloMovimiento: boolean;
  /**
   * Esconder las desactivadas.
   *
   * ▶ Arranca en `false` A PROPÓSITO. La cabecera de la pantalla dice por qué
   * las desactivadas se ven: esconderlas haría que desactivar pareciera
   * borrar. Un filtro que las esconda **por omisión** deshace esa decisión sin
   * que nadie la discuta. Que lo pida quien lo quiera.
   */
  readonly ocultarInactivas: boolean;
}

export interface CuentasFiltradas<T> {
  /** Lo que se pinta: las que coinciden **más sus padres**, en el orden original. */
  readonly visibles: readonly T[];
  /** Los códigos que de verdad coincidieron. Los demás son estructura. */
  readonly coincidieron: ReadonlySet<string>;
  /** Cuántas coincidieron. No es `visibles.length`, y confundirlos sería mentir. */
  readonly cuantas: number;
}

export const SIN_CRITERIO: CriterioDeCuentas = {
  texto: '', tipo: '', soloMovimiento: false, ocultarInactivas: false,
};

/** ¿Hay algún filtro puesto? Con ninguno, el conteo del encabezado es el total. */
export function hayCriterio(c: CriterioDeCuentas): boolean {
  return c.texto.trim() !== '' || c.tipo !== '' || c.soloMovimiento || c.ocultarInactivas;
}

/**
 * El catálogo filtrado, **sin perder el árbol**.
 *
 * ▶ LOS PADRES DE LO QUE COINCIDE VUELVEN, aunque ellos no coincidan.
 *
 * La pantalla dibuja la sangría por la cadena de padres. Si se filtran las
 * filas a secas, una cuenta que coincide aparece sangrada tres niveles bajo
 * nada: **la sangría pasa a decir una profundidad que no se puede ver.** Y
 * peor, `1101-01 Caja chica` sin su `1101 Caja general` encima es un código
 * huérfano en una pantalla cuya estructura es el dato.
 *
 * Por eso vuelven — y por eso vuelven MARCADOS. Contar los padres como
 * resultados diría «ocho cuentas» de una búsqueda que encontró dos, que es la
 * familia del guardián 13: un número que se lee como otra cosa.
 */
export function filtrarCuentas<T extends CuentaFiltrable>(
  cuentas: readonly T[], criterio: CriterioDeCuentas,
): CuentasFiltradas<T> {
  const coincidieron = new Set<string>();

  for (const c of cuentas) {
    if (criterio.tipo !== '' && c.tipo !== criterio.tipo) continue;
    if (criterio.soloMovimiento && !c.permiteMovimiento) continue;
    if (criterio.ocultarInactivas && !c.activa) continue;
    // ▶ Los DOS idiomas, siempre. Un catálogo bilingüe se conoce por el nombre
    // que cada quien aprendió, y buscar «payroll» leyendo en español tiene que
    // encontrar «Salarios». El rótulo del campo lo dice, para que la fila que
    // aparece sin que se vea por qué tenga explicación a la vista.
    if (!coincide(criterio.texto, c.codigo, c.nombre, c.nombreEn)) continue;
    coincidieron.add(c.codigo);
  }

  if (!hayCriterio(criterio)) {
    return { visibles: cuentas, coincidieron, cuantas: coincidieron.size };
  }

  // Los padres, subiendo por la cadena. Se recorre desde cada coincidencia
  // hacia arriba y no al revés: un `padre` que apunta a una cuenta que no está
  // en la lista corta la subida sin dar una vuelta infinita.
  const porCodigo = new Map(cuentas.map((c) => [c.codigo, c]));
  const visibles = new Set(coincidieron);
  for (const codigo of coincidieron) {
    let actual = porCodigo.get(codigo)?.padre ?? null;
    while (actual !== null && !visibles.has(actual)) {
      visibles.add(actual);
      actual = porCodigo.get(actual)?.padre ?? null;
    }
  }

  return {
    // El orden original, que es el del código. Reconstruirlo desde el conjunto
    // daría un árbol desordenado.
    visibles: cuentas.filter((c) => visibles.has(c.codigo)),
    coincidieron,
    cuantas: coincidieron.size,
  };
}

/* ═══════════════════════════════════════════════════════════════════════════
   La consola
   ═══════════════════════════════════════════════════════════════════════════ */

/** Lo mínimo que el filtro de la consola necesita ver de una empresa. */
export interface EmpresaFiltrable {
  readonly codigo: string;
  readonly razon_social: string;
  readonly identificacion: string | null;
  readonly estado: string;
  readonly es_demo: boolean;
}

export interface CriterioDeEmpresas {
  readonly texto: string;
  /** `activa`, `suspendida`, `cerrada`, o `''` para todos. */
  readonly estado: string;
  /**
   * Qué hacer con las de demostración.
   *
   * ▶ Tres valores y no una casilla, porque son tres preguntas distintas y
   * las tres se hacen: «la cartera de verdad» (`sin`), «enséñame la demo para
   * la presentación» (`solo`), y `todas`. Con una casilla, «solo las demo»
   * —que es para lo que la demo existe— no se puede pedir.
   *
   * Arranca en `todas`: esconder algo por omisión en la pantalla que sirve
   * para administrar es cómo se pierde una empresa suspendida.
   */
  readonly demo: 'todas' | 'sin' | 'solo';
}

export const SIN_CRITERIO_DE_EMPRESAS: CriterioDeEmpresas = {
  texto: '', estado: '', demo: 'todas',
};

export function hayCriterioDeEmpresas(c: CriterioDeEmpresas): boolean {
  return c.texto.trim() !== '' || c.estado !== '' || c.demo !== 'todas';
}

/**
 * Las empresas del operador, filtradas.
 *
 * No hay árbol que preservar acá, así que es una lista y ya. Lo que sí se
 * mantiene es la regla del conteo: quien pinta cuántas hay tiene que decir
 * **si están filtradas**, o el número dice otra cosa de la que parece.
 */
export function filtrarEmpresas<T extends EmpresaFiltrable>(
  empresas: readonly T[], criterio: CriterioDeEmpresas,
): readonly T[] {
  return empresas.filter((e) => {
    if (criterio.estado !== '' && e.estado !== criterio.estado) return false;
    if (criterio.demo === 'sin' && e.es_demo) return false;
    if (criterio.demo === 'solo' && !e.es_demo) return false;
    // La cédula entra en la búsqueda: es por donde se busca a un cliente
    // cuando llega un pago y lo único que trae es el número.
    return coincide(criterio.texto, e.codigo, e.razon_social, e.identificacion);
  });
}

/**
 * La cuenta que HOY recibe movimiento bajo un código, o `null`.
 *
 * ▶▶ EL CÓDIGO DEL CATÁLOGO MODELO ES UN PREFIJO, NO UNA CUENTA.
 *
 * Desde la `0093` una empresa nace con un tercer nivel: `1110` es de mayor y la
 * que recibe es `1110-01`. Buscar con igualdad exacta **encuentra** la `1110` y
 * la enseña — o sea, le nombra al cliente una cuenta contra la que no puede
 * registrar nada. Es el mismo defecto que la base tuvo en once sitios el 14 de
 * setiembre, y acá no rompe un asiento: rompe una frase, que se descubre más
 * tarde y por una persona.
 *
 * ▶ El prefijo exacto gana cuando recibe, porque el guion ordena antes que
 * cualquier dígito: una empresa de dos niveles sigue viendo su cuenta y una de
 * tres ve su auxiliar, con la misma regla y sin una bandera que las distinga.
 *
 * ▶ Vive acá y no en `catalogo.tsx` por una razón de herramienta: el corredor
 * de pruebas de Node no importa `.tsx`, así que una función pura metida ahí
 * **no se puede probar** — y ésta es justo de las que se prueban con tres
 * catálogos de mentira y ninguna pantalla.
 *
 * ▶ Y es la MISMA regla que `control.cuenta_de_antiguedad` (0097), escrita otra
 * vez porque esto es una etiqueta y aquello un asiento: no se puede pedir a la
 * base el rótulo de una pantalla. Lo que no puede haber son dos RESPUESTAS —
 * por eso el orden y el filtro son literalmente los mismos.
 */
export function cuentaQueRecibe<T extends CuentaFiltrable>(
  cuentas: readonly T[], codigo: string,
): T | null {
  return cuentas
    .filter((c) => c.codigo.startsWith(codigo) && c.permiteMovimiento && c.activa)
    .toSorted((a, b) => a.codigo.localeCompare(b.codigo))[0] ?? null;
}
